import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { splunkInstance } from '@/common/services/splunk';
import { buildSubstituteDoctorSearchText } from '@/common/utils/buildSubstituteDoctorSearch';

interface OnlineVisitSuggestionBannerProps {
  expertises: any;
  doctorCity?: string;
  slug: string;
}

/**
 * Shown under the booking card when the doctor has no bookable channel at all — in-person or
 * online — replacing the old named substitute-doctor suggestion there for the flagged population
 * (owner rollout, 2026-10-17, phase 1). Deliberately carries no other doctor's name here: the
 * owner asked that this doctor never be made to look replaced on their own profile. Naming the
 * substitute is deferred to /online-visit-suggestion, one click away.
 *
 * View + click events both fire here (not just click) so the owner can later compute a real
 * click-through rate, not just a raw click count with an unknown denominator.
 */
const OnlineVisitSuggestionBanner = ({ expertises, doctorCity, slug }: OnlineVisitSuggestionBannerProps) => {
  const groupName = expertises?.group_expertises?.[0]?.name;
  const text = buildSubstituteDoctorSearchText(expertises?.expertises);
  const href = `/online-visit-suggestion/?${new URLSearchParams({
    text,
    city: doctorCity || 'ir',
    ...(groupName ? { group: groupName } : {}),
    from: slug,
  }).toString()}`;

  const viewSentFor = useRef<string | null>(null);
  useEffect(() => {
    if (!slug || viewSentFor.current === slug) return;
    viewSentFor.current = slug;
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-view-banner',
      event: { slug, variant: 'no-online-visit-banner' },
    });
  }, [slug]);

  const handleClick = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-banner',
      event: { slug, variant: 'no-online-visit-banner' },
    });
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      className="block rounded-xl border border-[#DCE5FF] bg-[#EFF3FF] p-4"
    >
      <div className="flex items-center gap-2.5 mb-2.5">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#DCE5FF] text-sm">💬</span>
        <span className="text-sm font-bold text-[#2A4FD9]">
          {groupName ? `یک متخصص ${groupName} دیگر` : 'یک متخصص دیگر'}، همین الان آنلاین پاسخگوست
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-2.5">اگر عجله دارید، می‌توانید با یک متخصص دیگر مشورت کنید</p>
      <ul className="flex flex-col gap-1.5 mb-3 text-xs text-slate-600">
        <li className="flex items-center gap-1.5">
          <span className="text-primary">✓</span>
          <span>همین الان، بدون نوبتِ روزها بعد</span>
        </li>
        <li className="flex items-center gap-1.5">
          <span className="text-primary">✓</span>
          <span>بدون رفت‌وآمد و بدون صف انتظار</span>
        </li>
        <li className="flex items-center gap-1.5">
          <span className="text-primary">✓</span>
          <span>پیگیری در همان گفت‌وگو، هر زمان که بخواهید</span>
        </li>
      </ul>
      <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2.5">
        <span className="text-sm font-semibold text-[#2A4FD9]">دیدن این متخصص و شروع ویزیت آنلاین</span>
        <span className="text-[#2A4FD9]">&larr;</span>
      </div>
    </Link>
  );
};

export default OnlineVisitSuggestionBanner;
